// =====================================================================
// Agent Studio — persona API client. Wraps the EXISTING brand-facts
// endpoint (GET/PUT /api/admin/brand) — the persona fields are stored
// as brand_facts keys (persona_warmth/formality/brevity/tone_tags/
// emoji_chat + agent_greeting), so there is no separate endpoint. The
// agent NAME lives in the branding store (/api/settings/branding).
// =====================================================================
import { api } from "@/lib/api";

export interface BrandFactsResponse {
  ok: boolean;
  facts: Record<string, string>;
  keys: string[];
}

export async function getBrandFacts(): Promise<BrandFactsResponse> {
  return api<BrandFactsResponse>("/api/admin/brand");
}

export async function putBrandFacts(
  facts: Record<string, string>,
): Promise<{ ok: boolean; updated_count: number }> {
  return api<{ ok: boolean; updated_count: number }>("/api/admin/brand", {
    method: "PUT",
    body: { facts },
  });
}

// §agent-baseline — the hardcoded rules the agent already follows (curated
// from the chat/voice prompts, drift-tested server-side), rendered inside
// the Routine editor. The ADJUSTABLE subset can be per-tenant switched off
// or re-worded (§agent-baseline-overrides): the override map is stored as
// JSON in brand_facts.baseline_overrides and applied to the built prompt.
export interface BaselineRule {
  id: string;
  text: string;
  channels: "chat" | "voice" | "both";
  category: string;
  /** §persona-vs-routine — personality/style rules render ONLY in the
   * Persona tab; business rules (honesty/orders/safety) in Routine. */
  surface: "persona" | "routine";
  /** can this rule be switched off / re-worded per tenant? */
  adjustable: boolean;
  /** adjustable AND re-wordable (false = toggle-only, e.g. protocol flows) */
  editable: boolean;
  /** current per-tenant state */
  enabled: boolean;
  custom_text: string;
}

export interface BaselineFlow {
  id: string;
  trigger: string;
  steps: string[];
  handoff: string;
  channels: string;
  surface: "persona" | "routine";
  adjustable: boolean;
  editable: boolean;
  enabled: boolean;
  custom_text: string;
}

export interface AgentBaseline {
  always: BaselineRule[];
  never: BaselineRule[];
  flows: BaselineFlow[];
  counts: { always: number; never: number; flows: number };
}

export async function getAgentBaseline(): Promise<AgentBaseline> {
  const r = await api<{ ok: boolean; baseline: AgentBaseline }>(
    "/api/admin/agent-baseline",
  );
  return r.baseline;
}

/** One override entry: on=false disables the rule; on=true + text re-words it. */
export interface BaselineOverride {
  on: boolean;
  text?: string;
}

/** Rebuild the full override map from the baseline payload (the server echoes
 * each rule's current state), then apply one change and save the whole map. */
export function overridesFromBaseline(b: AgentBaseline): Record<string, BaselineOverride> {
  const out: Record<string, BaselineOverride> = {};
  for (const r of [...b.always, ...b.never, ...b.flows]) {
    if (!r.adjustable) continue;
    if (!r.enabled) out[r.id] = { on: false };
    else if ((r.custom_text || "").trim()) out[r.id] = { on: true, text: r.custom_text };
  }
  return out;
}

export async function saveBaselineOverrides(
  overrides: Record<string, BaselineOverride>,
): Promise<void> {
  await putBrandFacts({ baseline_overrides: JSON.stringify(overrides) });
}

// Agent name — branding store. GET returns { branding: { agent_name, ... } };
// POST takes flat { agent_name, ... }.
export interface Branding {
  agent_name?: string;
  app_name?: string;
  company_name?: string;
  // §brand-identity (2026-08-20) — the tenant's visual identity.
  // `brand_primary` is "" when unset: the dashboard keeps its own accent
  // rather than guessing one. `logo_url` is null when no logo is stored —
  // never a path, and never a URL that would 404 into a broken image.
  brand_primary?: string;
  logo_url?: string | null;
}

/** Upload a logo. Returns the refreshed branding so callers can re-render
 *  without a second round-trip. */
export async function uploadBrandLogo(file: File): Promise<Branding> {
  const form = new FormData();
  form.append("file", file);
  const res = await api<{ branding: Branding }>(
    "/api/settings/branding/logo",
    { method: "POST", body: form },
  );
  return res.branding ?? {};
}

/** §industry-behaviour (2026-08-20) — the industries the platform serves.
 *  Fetched, never hardcoded: the `vertical` fact's own help text still lists a
 *  retired six-value vocabulary, and a picker copied from it would offer values
 *  that resolve to NO pack — silently stripping a tenant of its tools,
 *  boundaries and selling posture. */
export interface IndustryPack {
  key: string;
  label: string;
  nouns: string[];
  boundary: string;
}

/** §home-currency (2026-08-20) — the business's OWN charge currency.
 *  Writes BOTH stores the resolver reads (payment_currency and the `currency`
 *  brand fact), so this control cannot silently lose to a stale
 *  payment_currency the way the Knowledge page's Currency box did. */
export async function putHomeCurrency(currency: string): Promise<string> {
  const res = await api<{ currency: string }>("/api/settings/currency", {
    method: "POST",
    body: { currency },
  });
  return res.currency ?? currency;
}

export async function getIndustryPacks(): Promise<IndustryPack[]> {
  const res = await api<{ packs: IndustryPack[] }>("/api/industry/packs");
  return res.packs ?? [];
}

export async function deleteBrandLogo(): Promise<Branding> {
  const res = await api<{ branding: Branding }>(
    "/api/settings/branding/logo",
    { method: "DELETE" },
  );
  return res.branding ?? {};
}
export async function getBranding(): Promise<Branding> {
  const res = await api<{ branding: Branding }>("/api/settings/branding");
  return res.branding ?? {};
}
export async function putBranding(patch: Partial<Branding>): Promise<void> {
  await api("/api/settings/branding", { method: "POST", body: patch });
}

/* ---- persona field helpers (the canonical key names + allowed values) ---- */
export const PERSONA_KEYS = {
  warmth: "persona_warmth",
  formality: "persona_formality",
  brevity: "persona_brevity",
  toneTags: "persona_tone_tags",
  emojiChat: "persona_emoji_chat",
  greeting: "agent_greeting",
  // §persona-vs-routine (2026-07-08) — sign-off + accent moved here from the
  // Knowledge › Business facts form: personality is edited ONLY in Persona.
  signoff: "agent_signoff",
  accent: "voice_english_accent",
  // §industry-behaviour (2026-08-20) — the tenant's trade, edited in Persona
  // beside the rest of the agent's character. Resolves to an industry pack:
  // tools, boundaries, nouns, and selling posture.
  vertical: "vertical",
} as const;

export const TONE_OPTIONS = [
  "Warm",
  "Playful",
  "Concise",
  "Reassuring",
  "Expert",
  "Casual",
  "Premium",
  "Patient",
] as const;
